/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { formatPriceInLocale } from "@lemmonhq/orders";
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
  useLocalization,
} from "@lemmonhq/uikit";
import { useAuth, useTrpc } from "@app/vue/providers/index";
import { EntriesModal } from "../components/EntriesModal";

type Account = {
  code: string;
  name: string;
  kind: string;
  category: string;
  is_active: boolean;
  debit_total: number;
  credit_total: number;
  signed_balance: number;
  entry_count: number;
};

type OverviewRow =
  | { type: "group"; kind: string; accountCount: number }
  | { type: "account"; account: Account };

const KIND_ORDER = [
  "asset",
  "liability",
  "revenue",
  "contra_revenue",
  "expense",
];

const KIND_ICONS: Record<string, string> = {
  asset: "account_balance_wallet",
  liability: "receipt_long",
  revenue: "trending_up",
  contra_revenue: "trending_down",
  expense: "payments",
};

// Credit-normal accounts read more naturally as positive numbers.
function normalBalance(account: Account): number {
  if (account.kind === "liability" || account.kind === "revenue") {
    return -account.signed_balance;
  }
  return account.signed_balance;
}

export const OverviewTab = defineComponent({
  name: "ChartOverviewTab",
  props: {
    from: { type: String, required: true },
    to: { type: String, required: true },
  },
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const auth = useAuth();
    const currency = () => auth.user.value?.restaurant.currency || "CHF";

    const chartQuery = useQuery({
      queryKey: computed(() => ["accounting", "chart", props.from, props.to]),
      queryFn: () =>
        trpc.accounting.chartOfAccounts.query({
          from: props.from,
          to: props.to,
        }),
    });

    const accountsByKind = computed(() => {
      const accounts = (chartQuery.data.value?.accounts ?? []) as Account[];
      const groups: Record<string, Account[]> = {};
      for (const acc of accounts) {
        (groups[acc.kind] ??= []).push(acc);
      }
      return KIND_ORDER.filter((k) => groups[k]?.length).map((kind) => ({
        kind,
        accounts: groups[kind],
      }));
    });

    const tableRows = computed<OverviewRow[]>(() =>
      accountsByKind.value.flatMap((group) => [
        {
          type: "group" as const,
          kind: group.kind,
          accountCount: group.accounts.length,
        },
        ...group.accounts.map((account) => ({
          type: "account" as const,
          account,
        })),
      ]),
    );

    const totals = computed(
      () =>
        chartQuery.data.value?.totals ?? { debit_total: 0, credit_total: 0 },
    );
    const isBalanced = computed(
      () =>
        Math.abs(totals.value.debit_total - totals.value.credit_total) < 0.005,
    );

    const activeAccount = ref<Account | null>(null);

    const buildColumns = (): ResponsiveTableColumn[] => [
      {
        key: "code",
        label: t("reports.coa.col_code"),
        render: (row: OverviewRow) => (
          <span class="font-mono text-xs text-base-content/60">
            {row.type === "account" ? row.account.code : ""}
          </span>
        ),
        mobilePriority: "secondary",
      },
      {
        key: "name",
        label: t("reports.coa.col_account"),
        render: (row: OverviewRow) =>
          row.type === "account" ? (
            <div class="flex flex-col">
              <span class="font-medium text-base-content">
                {row.account.name}
              </span>
              <span class="text-[11px] text-base-content/45 md:hidden font-mono">
                {row.account.code} · {row.account.category}
              </span>
            </div>
          ) : null,
        mobilePriority: "primary",
      },
      {
        key: "category",
        label: t("reports.coa.col_category"),
        render: (row: OverviewRow) =>
          row.type === "account" ? (
            <span class="badge badge-ghost badge-sm font-mono text-[10px]">
              {row.account.category}
            </span>
          ) : null,
        mobilePriority: "hidden",
      },
      {
        key: "debit",
        label: t("reports.coa.col_debit"),
        headerClass: "text-right",
        cellClass: "text-right tabular-nums",
        render: (row: OverviewRow) =>
          row.type === "account" && row.account.debit_total
            ? formatPriceInLocale(row.account.debit_total, false, 2, currency())
            : "—",
        mobilePriority: "secondary",
      },
      {
        key: "credit",
        label: t("reports.coa.col_credit"),
        headerClass: "text-right",
        cellClass: "text-right tabular-nums",
        render: (row: OverviewRow) =>
          row.type === "account" && row.account.credit_total
            ? formatPriceInLocale(
                row.account.credit_total,
                false,
                2,
                currency(),
              )
            : "—",
        mobilePriority: "secondary",
      },
      {
        key: "balance",
        label: t("reports.coa.col_balance"),
        headerClass: "text-right",
        cellClass: "text-right tabular-nums",
        render: (row: OverviewRow) => {
          if (row.type === "group") return null;
          const bal = normalBalance(row.account);
          return (
            <span
              class={`font-semibold ${bal < 0 ? "text-error" : "text-base-content"}`}
            >
              {formatPriceInLocale(bal, true, 2, currency())}
            </span>
          );
        },
        mobilePriority: "primary",
        mobilePrimaryClass: "text-right shrink-0",
      },
    ];

    const renderKpis = () => (
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div class="rounded-2xl border border-base-300/60 bg-base-200/40 p-4">
          <div class="text-[11px] font-semibold uppercase tracking-wider text-base-content/45">
            {t("reports.coa.kpi_debits")}
          </div>
          <div class="mt-1 text-xl font-semibold tabular-nums text-base-content">
            {formatPriceInLocale(totals.value.debit_total, true, 2, currency())}
          </div>
        </div>
        <div class="rounded-2xl border border-base-300/60 bg-base-200/40 p-4">
          <div class="text-[11px] font-semibold uppercase tracking-wider text-base-content/45">
            {t("reports.coa.kpi_credits")}
          </div>
          <div class="mt-1 text-xl font-semibold tabular-nums text-base-content">
            {formatPriceInLocale(
              totals.value.credit_total,
              true,
              2,
              currency(),
            )}
          </div>
        </div>
        <div class="col-span-2 sm:col-span-1 rounded-2xl border border-base-300/60 bg-base-200/40 p-4 flex items-center gap-3">
          <span
            class={`material-symbols-outlined text-3xl ${
              isBalanced.value ? "text-success" : "text-warning"
            }`}
          >
            {isBalanced.value ? "check_circle" : "warning"}
          </span>
          <div>
            <div class="text-[11px] font-semibold uppercase tracking-wider text-base-content/45">
              {t("reports.coa.kpi_status")}
            </div>
            <div class="mt-0.5 text-sm font-medium text-base-content">
              {isBalanced.value
                ? t("reports.coa.kpi_balanced")
                : t("reports.coa.kpi_unbalanced")}
            </div>
          </div>
        </div>
      </div>
    );

    return () => (
      <div class="flex flex-col gap-4">
        {renderKpis()}

        {chartQuery.isError.value ? (
          <div class="flex flex-col items-center justify-center h-64 text-error gap-3">
            <span class="material-symbols-outlined text-5xl opacity-60">
              error_outline
            </span>
            <p class="text-sm font-medium">{t("reports.coa.error_load")}</p>
            <button
              class="btn btn-sm btn-outline btn-error"
              onClick={() => chartQuery.refetch()}
            >
              <span class="material-symbols-outlined text-base">refresh</span>
              {t("reports.coa.retry")}
            </button>
          </div>
        ) : chartQuery.isLoading.value ? (
          <div class="flex justify-center items-center py-20">
            <span class="loading loading-spinner loading-lg text-primary opacity-60" />
          </div>
        ) : accountsByKind.value.length === 0 ? (
          <div class="flex flex-col items-center justify-center py-20 gap-3 text-base-content/40">
            <span class="material-symbols-outlined text-5xl opacity-40">
              account_tree
            </span>
            <p class="text-sm">{t("reports.coa.empty_accounts")}</p>
          </div>
        ) : (
          <section class="rounded-2xl border border-base-300/60 bg-base-100 overflow-hidden shadow-sm">
            <ResponsiveTable
              columns={buildColumns()}
              rows={tableRows.value}
              rowKey={(row: OverviewRow) =>
                row.type === "group" ? `group-${row.kind}` : row.account.code
              }
              renderFullWidthRow={(row: OverviewRow) =>
                row.type === "group" ? (
                  <div class="flex items-center gap-2 px-4 py-3 bg-base-200/50 border-b border-base-300/50">
                    <span class="material-symbols-outlined text-base text-primary">
                      {KIND_ICONS[row.kind] ?? "folder"}
                    </span>
                    <h2 class="text-sm font-semibold uppercase tracking-wide text-base-content/70">
                      {t(`reports.coa.kind_${row.kind}`)}
                    </h2>
                    <span class="ml-auto text-xs text-base-content/40">
                      {row.accountCount}
                    </span>
                  </div>
                ) : null
              }
              onRowClick={(row: OverviewRow) => {
                if (row.type === "account") activeAccount.value = row.account;
              }}
            />
          </section>
        )}

        {activeAccount.value && (
          <EntriesModal
            title={activeAccount.value.name}
            code={activeAccount.value.code}
            category={activeAccount.value.category}
            accountCode={activeAccount.value.code}
            from={props.from}
            to={props.to}
            onClose={() => (activeAccount.value = null)}
          />
        )}
      </div>
    );
  },
});
